import { FlatTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, input, signal, ViewEncapsulation } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';

interface TreeNode {
    value: string;
    id: string;
    parentId?: string;
    children?: TreeNode[];
}

interface FlatTreeNode {
    value: string;
    id: string;
    parentId?: string;
    level: number;
    expandable: boolean;
    selected: boolean;
}

@Component({
    selector: 'st-tree',
    templateUrl: './tree.html',
    styleUrls: ['./tree.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-tree',
    },
})
export class TreeComponent extends EnpsControlComponent<string> implements AfterViewInit {
    private _transformer = (node: TreeNode, level: number) => ({
        expandable: !!node.children && node.children.length > 0,
        value: node.value,
        id: node.id,
        parentId: node.parentId,
        level,
        selected: node.id === this.value(),
    });

    treeControl = new FlatTreeControl<FlatTreeNode>(
        node => node.level,
        node => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        node => node.children
    );
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    hasChild = (_: number, node: FlatTreeNode) => node.expandable;

    itemList = input<TreeNode[]>([]);
    panelOpen = signal(false);

    _canOpen = computed(() => {
        return !this.panelOpen() && !this.disabled();
    });

    internalValue = signal<string>('');

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set(this.value() || '');
                if (this.itemList()) {
                    this.dataSource.data = this.itemList();
                }
            },
            { allowSignalWrites: true }
        );
    }

    toggle(): void {
        if (this.panelOpen()) {
            this.close();
            return;
        }
        this.open();
    }

    /** Opens the overlay panel. */
    open(): void {
        if (this._canOpen()) {
            this.panelOpen.set(true);
            this._calculateSelectedNode();
        }
    }

    /** Closes the overlay panel and focuses the host element. */
    close(): void {
        if (this.panelOpen()) {
            this.panelOpen.set(false);
        }
    }

    displayValue = computed(() => {
        return this.internalValue();
    });

    hasChildren(_: number, node) {
        return node.Children.length > 0;
    }

    private _calculateSelectedNode() {
        this.treeControl.dataNodes.forEach(n => (n.selected = false));
        const node = this.treeControl.dataNodes.find(n => n.id === this.internalValue());
        if (node) {
            node.selected = true;
            this._expandParents(node);
        }
    }

    handleSelected(node: FlatTreeNode) {
        this.internalValue.set(node.value);
        this.changed.emit(this.internalValue());
        this.close();
    }

    private _expandParents(node: FlatTreeNode) {
        const nodeIdx = this.treeControl.dataNodes.findIndex(n => n.id === node.id);

        if (nodeIdx === -1) {
            return;
        }

        for (let i = nodeIdx; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];
            this.treeControl.expand(currentNode);
            if (currentNode.level === 0) {
                break;
            }
        }
    }
}
